/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut, sendPasswordResetEmail, type User } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { getUser } from '../lib/data';
import type { AppUser } from '../types';

interface AuthValue {
  firebaseUser: User | null; profile: AppUser | null; loading: boolean;
  login(email: string, password: string): Promise<void>; logout(): Promise<void>; resetPassword(email: string): Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => onAuthStateChanged(auth, async (user) => {
    setFirebaseUser(user);
    setProfile(user ? await getUser(user.uid) : null);
    setLoading(false);
  }), []);

  const value = useMemo<AuthValue>(() => ({
    firebaseUser, profile, loading,
    login: async (email, password) => { await signInWithEmailAndPassword(auth, email.trim().toLowerCase(), password); },
    logout: () => signOut(auth),
    resetPassword: async (email) => { await sendPasswordResetEmail(auth, email.trim().toLowerCase()); },
  }), [firebaseUser, profile, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('AuthProvider is missing');
  return value;
}
